import { supabase } from "@/integrations/supabase/client";
import { sessionBackupStore } from "@/lib/sessionBackupStore";
import { clearSwAuthToken } from "@/lib/swAuthBridge";
import { revokeAccessCache } from "@/lib/offlineAccessCache";

const ACCESS_CACHE_PREFIXES = ["v2", "v3", "v4", "v5", "v6", "v7", "v8", "v9"].map((version) => `llx:access:${version}:`);
const DENY_CACHE_PREFIXES = ["v2", "v3", "v4", "v5", "v6", "v7", "v8", "v9"].map((version) => `llx:access-deny:${version}:`);
const FORCE_BETA_REAUTH_STORAGE_PREFIXES = ["llx:beta-ack-reauth:v1:"];
const BETA_ACK_FRESH_SIGN_IN_PREFIX = "llx:beta-ack-fresh-signin:v1:";
const BETA_ACK_FRESH_SIGN_IN_TTL_MS = 1000 * 60 * 15;

export const getBetaAcknowledgmentStatus = async (userId: string) => {
  const [betaTesterResult, adminResult, testAccountResult] = await Promise.all([
    supabase.from("beta_testers").select("id, agreement_signed_at").eq("user_id", userId).maybeSingle(),
    supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
    supabase.from("test_accounts").select("id").eq("user_id", userId).eq("is_active", true).maybeSingle(),
  ]);

  const gateCheckError = betaTesterResult.error || adminResult.error || testAccountResult.error;
  const isBetaTester = !!betaTesterResult.data;
  const isAdmin = !!adminResult.data;
  const isTestAccount = !!testAccountResult.data;
  const agreementSignedAt = betaTesterResult.data?.agreement_signed_at ?? null;

  return {
    gateCheckError,
    isBetaTester,
    isAdmin,
    isTestAccount,
    agreementSignedAt,
    isUnsignedBetaTester: isBetaTester && !isAdmin && !isTestAccount && !agreementSignedAt,
  };
};

export const clearBetaAccessCaches = (userId: string) => {
  try {
    ACCESS_CACHE_PREFIXES.forEach((prefix) => localStorage.removeItem(`${prefix}${userId}`));
    DENY_CACHE_PREFIXES.forEach((prefix) => sessionStorage.removeItem(`${prefix}${userId}`));
    FORCE_BETA_REAUTH_STORAGE_PREFIXES.forEach((prefix) => localStorage.removeItem(`${prefix}${userId}`));
  } catch {
    // Ignore storage errors; auth sign-out remains the source of truth.
  }
  revokeAccessCache(userId);
};

export const hasForcedBetaAcknowledgmentReauth = (userId: string) => {
  try {
    const raw = sessionStorage.getItem(`${BETA_ACK_FRESH_SIGN_IN_PREFIX}${userId}`);
    const signedInAt = raw ? Number(raw) : 0;
    if (!Number.isFinite(signedInAt) || Date.now() - signedInAt >= BETA_ACK_FRESH_SIGN_IN_TTL_MS) {
      sessionStorage.removeItem(`${BETA_ACK_FRESH_SIGN_IN_PREFIX}${userId}`);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const markBetaAcknowledgmentFreshSignIn = (userId: string) => {
  try {
    FORCE_BETA_REAUTH_STORAGE_PREFIXES.forEach((prefix) => localStorage.removeItem(`${prefix}${userId}`));
    sessionStorage.setItem(`${BETA_ACK_FRESH_SIGN_IN_PREFIX}${userId}`, Date.now().toString());
  } catch {
    // ignore storage errors
  }
};

export const forceBetaAcknowledgmentReauth = async (userId: string) => {
  clearBetaAccessCaches(userId);
  try {
    sessionStorage.removeItem(`${BETA_ACK_FRESH_SIGN_IN_PREFIX}${userId}`);
  } catch {
    // ignore storage errors
  }
  await sessionBackupStore.clearBackup();
  await clearSwAuthToken();
  await supabase.auth.signOut({ scope: "local" });
};

/**
 * Perform a full document reload to the given target path.
 *
 * Used after a beta acknowledgment / reauth redirect so the installed PWA
 * cannot keep the previous dashboard React tree, in-memory caches, or stale
 * service worker controlled document alive. This forces a fresh HTML fetch,
 * a fresh SW activation check, and a fresh module graph.
 */
export const hardReloadToBetaGate = async (targetPath: string) => {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.update().catch(() => undefined)));
    }
  } catch {
    // ignore — best-effort SW refresh
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => /workbox|precache|html|pages/i.test(k))
          .map((k) => caches.delete(k).catch(() => undefined)),
      );
    }
  } catch {
    // ignore — cache eviction is best-effort
  }
  try {
    window.location.replace(targetPath);
  } catch {
    window.location.href = targetPath;
  }
};